import {
  type InfiniteData,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
  useQuery
} from '@tanstack/react-query'
import type {
  ApplicantInfosLDResponse,
  ApplicantInfosResponse
} from '~/api/queries/applicant'
import {
  type ApiResponseError,
  fetchApiWithToken,
  hydraNextPageParam,
  type WithHydraPagination
} from '~/api/utils'
import type { ApplicantHeadData } from '~/domains/applicants/types'
import type { AttachmentResponse } from '~/domains/attachments/types'
import type { CompanyHead } from '~/domains/companies/types'
import type { ContactHeadData } from '~/domains/contacts/types'
import type { OrderHead } from '~/domains/orders/types'
import type { TaxonomyItem, TaxonomyItems } from '~/domains/taxonomies/types'
import type { UserHeadResponse } from '~/domains/users/types'
import type { AddressResponse } from '~/types/global'

export const orderStatus = [
  { id: 0, name: 'Fermée' },
  { id: 1, name: 'Ouverte' },
  { id: 2, name: 'Abandonnée' }
]

export const orderFinalStatusDetail = [
  { id: 1, name: 'Servie par nous' },
  { id: 2, name: 'Servie par concurrence' },
  { id: 3, name: 'Annulée par nous' },
  { id: 4, name: 'Annulée par client' }
]

export type OrderInfosResponse = {
  id: number
  qualification?: TaxonomyItem<'qualifications'>
  qualifications: TaxonomyItems<'qualifications'>
  jobTitle?: string
  positionDescription?: string
  company: CompanyHead
  contact: ContactHeadData
  urgency?: number
  status: number
  statusDetail: number
  created: string
  address?: {
    id: number
    line1?: string
    line2?: string
    postcode?: string
    city?: string
    country?: string
  }
  applicants: {
    applicant: ApplicantHeadData
    status: TaxonomyItem<'applicant_order_statuses'>
    created: string
    modified: string
    note: string | null
  }[]
  users: UserHeadResponse[]
  specialities: TaxonomyItems<'specialities'>
  segmentations: TaxonomyItems<'segmentations'>
  salaryMin: number
  salaryMax: number
  salaryCurrency: number | null
  studyLevel: TaxonomyItem<'study_levels'> | null
  studyYearsLow: number | null
  studyYearsHigh: number | null
  location: string
  locationCenter: {
    address: string
    geometry: {
      location: {
        lat: number
        lng: number
      }
    }
  }
  locationCodes: {
    postcode: string | null
    countrycode: string | null
  }
  locationRadius: number
  contractTerm: TaxonomyItem<'contract_terms'> | null
  contractStart: string
  contractEnd: string
  reason: TaxonomyItem<'reasons'> | null
  orderReason: string | null
  authorizations: TaxonomyItems<'authorizations'>
  languages: {
    language: TaxonomyItem<'languages'>
    languageLevel: number[] | null
  }[]
  softwares: {
    software: TaxonomyItem<'softwares'>
    softwareLevel: number[] | null
  }[]
  experiences: TaxonomyItems<'experiences'>
  applicantAcceptedCount: number
  applicantWantedCount: number
  employeeStatus?: TaxonomyItem<'employee_statuses'> | null
  management?: boolean | null
  referenceSalary?: number | null
  referenceSalaryCurrency?: number | null
  workingDays?: boolean
  workDurationPeriod?: 'hebdo' | 'mensuel'
  workDurationValue?: string | null
  referenceSalaryMonths?: number | null
  schedule?: string | null
  flexibility?: number | null
  probationPeriod?: number | null
  warrantyDuration?: string | null
  nonWorkedPeriods?: string | null
  bonus?: string | null
  mealVoucher?: boolean | null
  mealVoucherValue?: number | null
  rttRecovery?: boolean | null
  holidays?: string | null
  otherRecovery?: boolean | null
  holidaysDetails?: string | null
  bonuses?: string | null
  hourlyRate?: string | null
  tempCoefficient?: string | null
  billingRate?: string | null
  commissionPercentage?: string | null
  flatRate?: string | null
  negotiatedConditions?: string | null
  feasibility?: number | null
  comment?: string
  agency?: string | null
  matchingLocation?: {
    address: string
    city: string
    postcode: string
    countrycode: string
    lat: number
    lng: number
  }
  hasMatchingLocationLegacy: boolean
  applicantCountByStatus: {
    id: number
    applicantCount: number
  }[]
  offers: string[]
  pendingApplicationsCount?: number
}

export type OrderCollectionReponse = Omit<OrderInfosResponse, 'applicants'>

export type OrderInfosLDResponse = WithHydraPagination<
  OrderInfosResponse | OrderCollectionReponse
>

export type OrderListFilters = {
  sort?: string | string[]
  sortDirection?: 'asc' | 'desc'
  user?: string
  status?: string
} & Record<string, string | string[] | undefined>

const ORDER_LIST_FILTER_TO_API: Record<string, string> = {
  status: 'status',
  agency: 'agency',
  urgency: 'urgency',
  user: 'users',
  company: 'contact.company.id',
  naf: 'contact.company.naf',
  qualification: 'qualifications',
  contractType: 'typeContrat',
  feasibility: 'faisabilite',
  segmentation: 'contact.company.segmentations',
  createdAfter: 'created[after]',
  createdBefore: 'created[before]',
  modifiedAfter: 'modified[after]',
  modifiedBefore: 'modified[before]',
  lastActivityDateAfter: 'lastActivityDate[after]',
  lastActivityDateBefore: 'lastActivityDate[before]'
}

const orderListFilterQueryKey = (barKey: string): string =>
  ORDER_LIST_FILTER_TO_API[barKey] ?? barKey

const applicantOrderNestedOrderFilterQueryKey = (barKey: string): string =>
  `order.${orderListFilterQueryKey(barKey)}`

export const useOrderListWithFilters = ({
  enabled = true,
  itemsPerPage = 30,
  filters = {}
}: {
  enabled?: boolean
  itemsPerPage?: number
  filters?: OrderListFilters
}): {
  orderlist: OrderCollectionReponse[] | undefined
  orderlistTotal: number | undefined
  isOrderListLoading: boolean
  isOrderListError: boolean
  isFetchNextOrderListPageError: boolean
  fetchNextOrderListPage: () => void
  hasNextOrderListPage: boolean
  isFetchingNextOrderListPage: boolean
} => {
  const ORDER_SORT_TO_API: Record<string, string> = {
    company: 'contact.company.name'
  }

  const { sort, sortDirection, ...restFilters } = filters
  const urlParams: Record<string, string> = {}

  Object.entries(restFilters).forEach(([key, value]) => {
    if (value !== undefined) {
      const apiKey = orderListFilterQueryKey(key)
      urlParams[apiKey] = Array.isArray(value) ? value.join(',') : value
    }
  })

  if (sort && sortDirection) {
    const sortArray = Array.isArray(sort) ? [...sort] : [sort]
    sortArray.forEach((sort, index) => {
      const apiSort = ORDER_SORT_TO_API[sort] ?? sort
      const key = `order[${apiSort}]`
      urlParams[key] = Array.isArray(sortDirection)
        ? sortDirection[index]
        : sortDirection
    })
  }

  const {
    data,
    isLoading: isOrderListLoading,
    fetchNextPage: fetchNextOrderListPage,
    hasNextPage: hasNextOrderListPage,
    isError,
    isFetchNextPageError,
    isFetchingNextPage: isFetchingNextOrderListPage
  } = useInfiniteQuery<
    OrderInfosLDResponse,
    Error,
    InfiniteData<OrderInfosLDResponse>
  >({
    queryKey: ['orders', 'search', urlParams],
    initialPageParam: 1,
    getNextPageParam: hydraNextPageParam(itemsPerPage),
    queryFn: async ({ pageParam, signal }) => {
      const orders = (await fetchApiWithToken({
        method: 'get',
        url: `/v1/orders?page=${pageParam}&itemsPerPage=${itemsPerPage}&${new URLSearchParams(urlParams)}`,
        useLDJson: true,
        signal
      })) as OrderInfosLDResponse
      return orders as OrderInfosLDResponse
    },
    refetchOnWindowFocus: false,
    staleTime: 1800_000,
    enabled
  })
  return {
    orderlist: data?.pages.flatMap((page) => page['hydra:member']),
    orderlistTotal: data?.pages[0]?.['hydra:totalItems'],
    isOrderListLoading,
    isOrderListError: isError,
    isFetchNextOrderListPageError: isFetchNextPageError,
    fetchNextOrderListPage,
    hasNextOrderListPage,
    isFetchingNextOrderListPage
  }
}

const useOrdersSearch = ({
  userId,
  companyId,
  search,
  ids,
  onlyOpen = false,
  enabled = true,
  fields
}: {
  userId?: number | null
  companyId?: number
  ids?: number[]
  search?: string
  enabled?: boolean
  onlyOpen?: boolean
  fields?: string[]
}) => {
  return useInfiniteQuery<
    OrderInfosLDResponse,
    Error,
    InfiniteData<OrderInfosLDResponse>
  >({
    queryKey: ['orders', 'search', { userId, companyId, search }, fields],
    initialPageParam: 1,
    getNextPageParam: hydraNextPageParam(),
    queryFn: async ({ pageParam, signal }) => {
      const params = new URLSearchParams()
      params.append('page', (pageParam as number).toString())
      if (userId) {
        params.append('users', userId.toString())
      }
      if (companyId) {
        params.append('contact.company.id', companyId.toString())
      }
      if (search) {
        params.append('q', search)
      }
      if (ids) {
        params.append('id[]', ids.join('&id[]='))
      }
      if (onlyOpen) {
        params.append('status', '1')
      }

      params.append('order[created]', 'desc')
      for (const field of fields ?? []) {
        params.append('fields[]', field)
      }

      return await fetchApiWithToken<OrderInfosLDResponse>({
        method: 'get',
        url: `/v1/orders?${params.toString()}`,
        useLDJson: true,
        signal
      })
    },
    enabled,
    staleTime: 60_000
  })
}

export type OrdersAutocompleteResponse = {
  id: number
  created: string
  jobTitle?: string
  qualifications: Array<{ id: number; name: string }>
  company: { id: number; name: string }
  matchingLocation?: AddressResponse
}

export const useOrdersAutocomplete = ({
  userId,
  companyId,
  search,
  ids,
  onlyOpen = false,
  enabled = true
}: {
  userId?: number | null
  companyId?: number
  ids?: number[]
  search?: string
  enabled?: boolean
  onlyOpen?: boolean
}) =>
  useOrdersSearch({
    userId,
    companyId,
    search,
    ids,
    onlyOpen,
    enabled,
    fields: [
      'id',
      'jobTitle',
      'qualifications',
      'company',
      'created',
      'matchingLocation'
    ]
  }) as unknown as UseInfiniteQueryResult<
    InfiniteData<WithHydraPagination<OrdersAutocompleteResponse>>
  >

export type MatchingFilter = {
  softwares?: { id: number; level?: number[] }[]
  languages?: { id: number; level?: number[] }[]
  experiences?: number[]
  contractTerm?: number
  salaryMin?: number
  salaryMax?: number
  location?: string
  locationRadius?: number
  locationCenter?: { lat: number; lng: number }
  locationCodes?: { postcode: string | null; countrycode: string | null }
  checkMobilitiesMode?: string
  authorizations?: number[]
  workedForUs?: boolean
  segmentations?: number[]
  studyYearsLow?: number
  studyYearsHigh?: number
  available?: boolean
  availableUnknown?: boolean
  orderBy?: 'score' | 'created'
}

export type MatchingFilterFlags = {
  proximityEnabled?: boolean
  mobilityEnabled?: boolean
  segmentationsEnabled?: boolean
  softwaresEnabled?: Set<number>
  languagesEnabled?: Set<number>
  experiencesEnabled?: boolean
  studyYearsEnabled?: boolean
  contractTermEnabled?: boolean
  salaryEnabled?: boolean
  authorizationsEnabled?: Set<number>
  available?: boolean
  availableUnknown?: boolean
  workedForUs?: boolean
}

export const buildMatchingFilter = (
  order: OrderInfosResponse,
  flags: MatchingFilterFlags
): MatchingFilter => {
  const filter: MatchingFilter = {}

  if (flags.available !== undefined) {
    filter.available = flags.available
    if (flags.available) {
      filter.availableUnknown = flags.availableUnknown ?? true
    }
  }

  if (flags.workedForUs) {
    filter.workedForUs = true
  }

  if (flags.proximityEnabled || flags.mobilityEnabled) {
    if (order.matchingLocation) {
      filter.locationCenter = {
        lat: order.matchingLocation.lat,
        lng: order.matchingLocation.lng
      }
      filter.locationCodes = {
        postcode: order.matchingLocation.postcode,
        countrycode: order.matchingLocation.countrycode
      }
      filter.locationRadius = order.locationRadius
      filter.checkMobilitiesMode =
        flags.proximityEnabled && flags.mobilityEnabled
          ? 'include'
          : flags.mobilityEnabled
            ? 'only'
            : ''
    }
  }

  if (flags.segmentationsEnabled && order.segmentations.length > 0) {
    filter.segmentations = order.segmentations.map((seg) => seg.id)
  }

  if (flags.softwaresEnabled && flags.softwaresEnabled.size > 0) {
    filter.softwares = order.softwares
      .filter((s) => flags.softwaresEnabled?.has(s.software.id))
      .map((s) => ({
        id: s.software.id,
        level: s.softwareLevel ?? undefined
      }))
  }

  if (flags.languagesEnabled && flags.languagesEnabled.size > 0) {
    filter.languages = order.languages
      .filter((l) => flags.languagesEnabled?.has(l.language.id))
      .map((l) => ({
        id: l.language.id,
        level: l.languageLevel ?? undefined
      }))
  }

  if (flags.experiencesEnabled && order.experiences.length > 0) {
    filter.experiences = order.experiences.map((exp) => exp.id)
  }

  if (flags.studyYearsEnabled) {
    filter.studyYearsLow = order.studyYearsLow ?? undefined
    filter.studyYearsHigh = order.studyYearsHigh ?? undefined
  }

  if (flags.contractTermEnabled && order.contractTerm) {
    filter.contractTerm = order.contractTerm.id
  }

  if (flags.salaryEnabled) {
    filter.salaryMin = order.salaryMin
    filter.salaryMax = order.salaryMax
  }

  if (flags.authorizationsEnabled && flags.authorizationsEnabled.size > 0) {
    filter.authorizations = Array.from(flags.authorizationsEnabled)
  }

  return filter
}

export const useOrder = (id?: number) =>
  useQuery<OrderInfosResponse, ApiResponseError>({
    queryKey: ['order', id],
    queryFn: ({ signal }) =>
      fetchApiWithToken<OrderInfosResponse>({
        method: 'get',
        url: `/v1/orders/${id}`,
        signal
      }),
    enabled: !!id
  })

const convertMatchingFilterToQueryParams = (filters: MatchingFilter) => {
  let queryParams = {}
  if (filters.softwares) {
    queryParams = {
      ...queryParams,
      softwares: filters.softwares
        .map(
          (software) =>
            `${software.id}${software.level ? `:${software.level.join(',')}` : ''}`
        )
        .join(',')
    }
  }
  if (filters.languages) {
    queryParams = {
      ...queryParams,
      languages: filters.languages
        .map(
          (language) =>
            `${language.id}${language.level ? `:${language.level.join(',')}` : ''}`
        )
        .join(',')
    }
  }
  if (filters.experiences) {
    queryParams = {
      ...queryParams,
      experiences: filters.experiences.join(',')
    }
  }
  if (filters.contractTerm) {
    queryParams = {
      ...queryParams,
      jobTypes: filters.contractTerm
    }
  }
  if (filters.salaryMin) {
    queryParams = {
      ...queryParams,
      'jobTypes.remunerationAccepted[gte]': filters.salaryMin
    }
  }
  if (filters.salaryMax) {
    queryParams = {
      ...queryParams,
      'jobTypes.remunerationAccepted[lte]': filters.salaryMax
    }
  }
  if (filters.studyYearsLow) {
    queryParams = {
      ...queryParams,
      'study[gte]': filters.studyYearsLow
    }
  }
  if (filters.studyYearsHigh) {
    queryParams = {
      ...queryParams,
      'study[lte]': filters.studyYearsHigh
    }
  }
  if (filters.locationRadius) {
    queryParams = {
      ...queryParams,
      'address[radius]': filters.locationRadius
    }
  }
  if (filters.locationCenter) {
    queryParams = {
      ...queryParams,
      'address[lat]': filters.locationCenter.lat,
      'address[lng]': filters.locationCenter.lng
    }
  }
  if (filters.locationCodes) {
    queryParams = {
      ...queryParams,
      'address[geo_postcode]': filters.locationCodes.postcode,
      'address[geo_countrycode]': filters.locationCodes?.countrycode
    }
  }
  if (filters.checkMobilitiesMode) {
    queryParams = {
      ...queryParams,
      'address[checkMobilitiesMode]': filters.checkMobilitiesMode ?? ''
    }
  }
  if (filters.authorizations) {
    queryParams = {
      ...queryParams,
      authorizations: filters.authorizations.join(',')
    }
  }
  if (filters.workedForUs) {
    queryParams = {
      ...queryParams,
      hasWorkedForUs: filters.workedForUs ? 'true' : 'false'
    }
  }
  if (filters.segmentations) {
    queryParams = {
      ...queryParams,
      segmentations: filters.segmentations.join(',')
    }
  }
  if (filters.available) {
    const now = new Date()
    queryParams = {
      ...queryParams,
      'unavailabilities[start]': now.toISOString(),
      ...(filters.availableUnknown !== false && {
        'unavailabilities[end]': new Date(
          new Date().setDate(now.getDate() + 1)
        ).toISOString()
      })
    }
  }
  if (filters.orderBy === 'created') {
    queryParams = { ...queryParams, matchingSort: 'created' }
  }
  return queryParams
}

export const useOrderApplicantMatching = (
  order?: OrderInfosResponse,
  filters: MatchingFilter = {},
  enabled = true
): {
  applicantMatching: ApplicantInfosResponse[] | undefined
  applicantMatchingTotal: number | undefined
  isApplicantMatchingLoading: boolean
  isApplicantMatchingError: boolean
  fetchNextApplicantPage: () => void
  hasNextApplicantPage: boolean
  isFetchingNextApplicantPage: boolean
} => {
  const {
    data,
    isLoading: isApplicantMatchingLoading,
    isError: isApplicantMatchingError,
    fetchNextPage: fetchNextApplicantPage,
    hasNextPage: hasNextApplicantPage,
    isFetchingNextPage: isFetchingNextApplicantPage
  } = useInfiniteQuery<
    ApplicantInfosLDResponse,
    Error,
    InfiniteData<ApplicantInfosLDResponse>
  >({
    queryKey: ['order', order?.id, 'matching', filters],
    initialPageParam: 1,
    getNextPageParam: hydraNextPageParam(),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        excludeApplicantsOfOrder: String(order?.id),
        orderMatchingId: String(order?.id),
        ...convertMatchingFilterToQueryParams(filters)
      })
      params.append(
        'qualifications',
        order?.qualifications.map((q) => String(q.id)).join(',') ?? ''
      )
      return fetchApiWithToken<ApplicantInfosLDResponse>({
        method: 'get',
        url: `/v1/applicants?page=${pageParam}&${params}`,
        useLDJson: true
      })
    },
    refetchOnWindowFocus: false,
    enabled: !!order && enabled,
    staleTime: 1800_000
  })
  return {
    applicantMatching: data?.pages.flatMap((page) => page['hydra:member']),
    applicantMatchingTotal: data?.pages[0]?.['hydra:totalItems'],
    isApplicantMatchingLoading,
    isApplicantMatchingError,
    fetchNextApplicantPage,
    hasNextApplicantPage,
    isFetchingNextApplicantPage
  }
}

type ApplicantIdsLDResponse = {
  'hydra:totalItems': number
  'hydra:member': number[]
}

export const useOrderApplicantMatchingIds = (
  order?: OrderInfosResponse,
  filters: MatchingFilter = {},
  enabled = true
): {
  applicantsIds: number[] | undefined
  isApplicantsIdsLoading: boolean
} => {
  const { data: applicantsIds, isLoading: isApplicantsIdsLoading } = useQuery<
    ApplicantIdsLDResponse,
    Error
  >({
    queryKey: ['order', order?.id, 'matching_ids', filters],
    queryFn: () => {
      const params = new URLSearchParams({
        excludeApplicantsOfOrder: String(order?.id),
        orderMatchingId: String(order?.id),
        ...convertMatchingFilterToQueryParams(filters)
      })
      params.append(
        'qualifications',
        order?.qualifications.map((q) => String(q.id)).join(',') ?? ''
      )
      return fetchApiWithToken({
        method: 'get',
        url: `/v1/applicants/ids?${params}`,
        useLDJson: true
      })
    },
    refetchOnWindowFocus: false,
    enabled: !!order && enabled,
    staleTime: 1800_000
  })
  return {
    applicantsIds: applicantsIds?.['hydra:member'],
    isApplicantsIdsLoading
  }
}

export type OrderHistoryEvent = {
  id: number
  created: string
  createdBy: string
  type:
    | 'status_update'
    | 'meeting_applicant'
    | 'meeting_schedule'
    | 'recall'
    | 'send_email'
    | 'sms_sent'
    | 'sms_received'
    | 'notes'
    | 'create_order'
  orderStatus: TaxonomyItem<'applicant_order_statuses'> | null
  notes: string | null
  isCancelled: boolean
  isCompleted: boolean | null
  scheduledDate: string | null
  completionDate: string | null
  assignedUser: string | null
  order: string
  applicants: string[]
  contacts: string[]
}

export type OrderHistoryEventLDResponse = {
  'hydra:member': OrderHistoryEvent[]
  'hydra:totalItems': number
}

export const useOrderHistory = ({
  orderId,
  applicantId
}: {
  orderId: number
  applicantId?: number
}): {
  items: OrderHistoryEvent[] | undefined
  itemsTotal: number | undefined
  isLoading: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
} => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<
      OrderHistoryEventLDResponse,
      Error,
      InfiniteData<OrderHistoryEventLDResponse>
    >({
      queryKey: ['order', orderId, 'history', applicantId],
      initialPageParam: 1,
      getNextPageParam: hydraNextPageParam(),
      queryFn: async ({ pageParam, signal }) =>
        await fetchApiWithToken<OrderHistoryEventLDResponse>({
          method: 'get',
          signal,
          url: `/v1/history?order=${orderId}&page=${pageParam}${applicantId ? `&applicant=${applicantId}` : ''}`,
          useLDJson: true
        }),
      enabled: !!orderId,
      staleTime: 300_000
    })
  return {
    items: data?.pages.flatMap((page) => page['hydra:member']),
    itemsTotal: data?.pages[0]?.['hydra:totalItems'],
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  }
}

export type TempoSyncPreview = {
  fields: { label: string; value: string }[]
  documents: { typeName: string; filename: string }[]
}

export type AccountingOrderDataResponse = {
  requiredInputs: string[]
  externalData: {
    canSendOrder: boolean
    syncStatus: string | null | undefined
    applicantSyncStatus: string | null | undefined
    contactSyncStatus: string | null | undefined
    companySyncStatus: string | null | undefined
    contactName: string | null | undefined
    companyName: string | null | undefined
    agencies: { id: number; name: string }[]
    agencyId: number | null
    contactId: number | null
    contacts: { id: number; name: string }[]
    interlocutorContactId: number | null
    externalQualifications: { id: number; name: string }[]
    externalQualificationId?: number | null
    externalMetadata: string | null
    applicantPreview: TempoSyncPreview | null | undefined
    contactPreview: TempoSyncPreview | null | undefined
    orderPreview: TempoSyncPreview | null | undefined
    companyPreview: TempoSyncPreview | null | undefined
  }
  warnings: {
    order: string[] | undefined
    applicant: string[] | undefined
  }
}

export const useAccountingOrderData = (
  orderId: number,
  accountingServiceEnabled: boolean,
  applicantId: number | undefined,
  agencyId?: number | null
) =>
  useQuery<AccountingOrderDataResponse, ApiResponseError>({
    queryKey: ['accountingOrderData', orderId, applicantId, agencyId],
    queryFn: () =>
      fetchApiWithToken<AccountingOrderDataResponse>({
        method: 'get',
        url: `/v1/accounting/order-data/${orderId}${applicantId ? `/${applicantId}` : ''}`
      }),
    staleTime: 1000 * 60 * 60 * 24,
    enabled: accountingServiceEnabled && orderId !== 0
  })

export type ApplicantOrderKanbanEntry = {
  id: number
  applicant: ApplicantHeadData
  order: OrderHead
  status: TaxonomyItem<'applicant_order_statuses'>
  created: string
  modified: string
  note: string | null
}

type ApplicantOrderKanbanLDResponse =
  WithHydraPagination<ApplicantOrderKanbanEntry>

export const useGlobalKanbanColumn = ({
  statusId,
  orderFilters = {},
  itemsPerPage = 30
}: {
  statusId: number
  orderFilters?: OrderListFilters
  itemsPerPage?: number
}): {
  entries: ApplicantOrderKanbanEntry[] | undefined
  total: number | undefined
  isLoading: boolean
  isError: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
} => {
  const { sort, sortDirection, ...restFilters } = orderFilters
  const urlParams: Record<string, string> = {}

  Object.entries(restFilters).forEach(([key, value]) => {
    if (value !== undefined) {
      const apiKey = applicantOrderNestedOrderFilterQueryKey(key)
      urlParams[apiKey] = Array.isArray(value) ? value.join(',') : value
    }
  })

  urlParams.status = `/v1/taxonomies/applicant_order_statuses/${statusId}`

  if (sort && sortDirection) {
    const sortArray = Array.isArray(sort) ? [...sort] : [sort]
    sortArray.forEach((s, index) => {
      const key = `order[order.${s}]`
      urlParams[key] = Array.isArray(sortDirection)
        ? sortDirection[index]
        : sortDirection
    })
  }

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery<
    ApplicantOrderKanbanLDResponse,
    Error,
    InfiniteData<ApplicantOrderKanbanLDResponse>
  >({
    queryKey: ['applicant_orders', 'kanban', statusId, urlParams],
    initialPageParam: 1,
    getNextPageParam: hydraNextPageParam(itemsPerPage),
    queryFn: async ({ pageParam, signal }) =>
      fetchApiWithToken<ApplicantOrderKanbanLDResponse>({
        method: 'get',
        url: `/v1/applicant_orders?page=${pageParam}&itemsPerPage=${itemsPerPage}&${new URLSearchParams(urlParams)}`,
        useLDJson: true,
        signal
      }),
    refetchOnWindowFocus: false,
    staleTime: 300_000
  })

  return {
    entries: data?.pages.flatMap((page) => page['hydra:member']),
    total: data?.pages[0]?.['hydra:totalItems'],
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  }
}

export const useOrderAttachments = ({
  orderId,
  enabled = true
}: {
  orderId?: number
  enabled?: boolean
}) =>
  useQuery({
    queryKey: ['order', orderId, 'attachments'],
    queryFn: ({ signal }) =>
      fetchApiWithToken<AttachmentResponse[]>({
        method: 'get',
        url: `/v1/orders/${orderId}/attachments`,
        signal
      }),
    refetchOnWindowFocus: false,
    enabled: (enabled ?? true) && !!orderId
  })
